import config from 'config';
import IPhoneMockupImage from 'assets/images/IPhone15Mockup.png';

import { FC, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
// i18next
import { useTranslation } from 'react-i18next';
// Icons
import { FaDesktop, FaSync, FaTimes, FaEye } from 'react-icons/fa';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setHash } from 'store/form/formSlice';
import { appActions } from 'store/app/appSlice';
import { selectQuizFormPreview } from 'store/app/appSelectors';
import { selectFormByQuizId, selectHash } from 'store/form/formSelectors';
import { RootState } from 'store';
import QuizTypes from 'types/QuizTypes';
// Components
import { IconButton } from 'components/Buttons';
import { Tooltip } from 'components/Tooltip';
import QuizFrame from 'components/QuizFrame';
import PreviewDesktopDialog from 'dialogs/PreviewDesktop.dialog';
// MUI
import { useMediaQuery } from '@material-ui/core';
// Utilties
import { encodeBase64 } from 'utilities/utilities';

type Props = {
  preview: 'quiz' | 'liveLeaderboard';
}

const QuizPreview:FC<Props> = ({ preview }) => {
  const { quizId } = useParams<{ quizId:string }>();

  const { t } = useTranslation();

  const isLaptop = useMediaQuery('(min-width: 1280px)');

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const quizFormPreview = useAppSelector(selectQuizFormPreview);
  const quizForm:any = useAppSelector((state:RootState) => selectFormByQuizId(state, { quizId }));
  const hash:string | null = useAppSelector(selectHash);

  const timeout = useRef<any>(null);

  const isPollQuizzes = quizForm && [QuizTypes.Poll, QuizTypes.RankingPoll].includes(quizForm.type);
  const isTeamSelectorQuiz = quizForm && quizForm.type === QuizTypes.TeamSelector;

  const [ dilaogPreviewDesktopDialog, setDialogPreviewDesktopOpen ] = useState(false)

  const togglePreviewDesktopDialog = () => setDialogPreviewDesktopOpen(!dilaogPreviewDesktopDialog);

  const handleClearTimeout = () => {
    if ( timeout.current ) clearTimeout(timeout.current);
  }

  useEffect(() => {
    return () => {
      handleClearTimeout();
    }
    // eslint-disable-next-line
  }, []);

  const handleReload = () => {
    handleClearTimeout();
    dispatch(setHash(null));
    timeout.current = setTimeout(() => {
      dispatch(setHash(encodeBase64(quizForm)))
    }, 1000);
  }

  const handleTogglePreview = () => {
    dispatch(appActions.toggleQuizFormPreview());
  }

  const handlePreview = () => {
    let endpoint = '';

    if (preview === 'quiz') {
      endpoint = `?u=${Date.now()}&preview=true`;
    } else if (isPollQuizzes) {
      endpoint = 'live-results';
    } else if (isTeamSelectorQuiz) {
      endpoint = 'live-teamboard';
    } else {
      endpoint = 'live-leaderboard';
    }

    window.open(`${config.quizURL}/quiz/${quizForm.quizNameSlug}/${endpoint}`);
  };

  const previewLabel = preview === 'quiz'
    ? t('components.quizPreview.previewQuiz')
      : isPollQuizzes
      ? t('components.quizPreview.previewLiveResults')
    : t('components.quizPreview.previewLiveLeaderboard');

  // if ( !quizForm ) return null;
  if (preview === 'liveLeaderboard' && !quizForm?.publicLeaderboard?.enabled ) return null;
  return (
    <div
      className={`
        laptop:flex
        bg-content z-40
        fixed laptop:relative
        top-0 laptop:top-auto
        left-0 laptop:left-auto
        h-full laptop:h-auto
        flex-shrink-0 rounded-xl
        ${quizFormPreview ? '' : 'hidden'}
        laptop:overflow-y-auto
      `}
      style={{
        borderColor: 'rgba(255,255,255,0.2)',
        width: isLaptop ? '305px' : '100%',
      }}
    >
      <div className="flex flex-col p-4 h-full w-full">
        <div className="flex items-center mb-2">
          <h3 className="text-white text-lg font-semibold text-center mr-4">{t('components.quizPreview.previewLabel')}</h3>
          <div className="flex-grow">
            <Tooltip className="ml-2" title={t('components.quizPreview.reloadPreviewButton')}>
              <IconButton
                onClick={handleReload}
              ><FaSync /></IconButton>
            </Tooltip>
            <Tooltip className="ml-2" title={t('components.quizPreview.desktopPreviewButton')}>
              <IconButton
                onClick={togglePreviewDesktopDialog}
              ><FaDesktop /></IconButton>
            </Tooltip>
            <Tooltip className="ml-2" title={previewLabel}>
              <IconButton
                onClick={handlePreview}
              ><FaEye /></IconButton>
            </Tooltip>
          </div>
          <Tooltip title={t('buttons.close')}>
            <IconButton
              className="flex items-center justify-center laptop:hidden"
              onClick={handleTogglePreview}
            ><FaTimes /></IconButton>
          </Tooltip>
        </div>
        {preview === 'quiz' && (
          <span className="block text-white text-sm font-light mb-2">{t('components.quizPreview.previewHint')}</span>
        )}
        <div className="flex-grow relative overflow-y-auto">
          <div className="absolute w-full h-full left-0 top-0">
            <div
              className="
                flex flex-col justify-center items-center
                relative my-4 mx-auto
              "
              style={{
                width: 420,
                height: 870,
                transform: isLaptop ? 'scale(0.65) translate(-27%, -28%)' : 'none',
              }}
            >
              <span
                className="
                  absolute top-0 left-0 w-full h-full
                  bg-contain bg-no-repeat
                  pointer-events-none
                  z-20
                "
                style={{
                  backgroundImage: `url(${IPhoneMockupImage})`
                }}
              ></span>
              {hash ? (
                <QuizFrame
                  style={{
                    width: '93%',
                    height: '97%',
                    borderRadius: '40px'
                  }}
                  id="preview-mobile"
                  quizNameSlug={quizForm?.quizNameSlug}
                  quizHash={hash}
                  preview={preview}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
      {dilaogPreviewDesktopDialog ? (
        <PreviewDesktopDialog
          open={dilaogPreviewDesktopDialog}
          onClose={togglePreviewDesktopDialog}
          quizNameSlug={quizForm?.quizNameSlug}
          quizHash={hash}
          preview={preview}
        />
      ) : null}
    </div>
  )
}

export default QuizPreview;
